/**
 * "Twin" resolution: the copies of an element a page draws for a visual
 * effect, which a CSS selector alone can miss.
 *
 * Pages routinely duplicate a piece of text: stripe.com stacks two `<h1>` in
 * the same grid cell (a `--background` copy and an aria-hidden `--foreground`
 * copy blended over it), sites keep a desktop and a mobile copy of a header,
 * carousels clone their slides. The trick only holds while every copy carries
 * the same text — a positional selector such as `h1:nth-of-type(1) > em`
 * reaches ONE copy, so personalizing it leaves the other copy showing the
 * original text on top of the new one.
 *
 * A twin of `element` is an element reached by the same relative path from a
 * same-tag sibling of one of its ancestors (`element` itself included, for
 * direct siblings), with the same tag and the same normalized text. Text
 * equality is what makes this safe to run on every apply pass: before the
 * first write every copy holds the original text, after it every copy holds
 * the personalized text, so twins keep resolving to the same set.
 *
 * Shared by the runtime SDK and the studio preview so both personalize the
 * same set of elements. Keep it dependency-free (it ships in the SDK bundle).
 */

/** Our own overlay nodes (studio portals); never a personalization target. */
const OVERLAY_SELECTOR = '[data-tinct-overlay]';

const DEFAULT_MAX_DEPTH = 3;

export type FindTwinElementsOptions = {
  /**
   * How many ancestors of the element to look at for same-tag siblings
   * (default 3). Bounds the work done per apply pass.
   */
  maxDepth?: number;
};

/** Whitespace-collapsed, trimmed text content — what "same text" means here. */
export function normalizeText(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

/** The descendant of `root` at `path` (child indices, top-down), if any. */
function followPath(root: Element, path: number[]): Element | null {
  let node: Element | null = root;
  for (const index of path) {
    node = node.children[index] ?? null;
    if (!node) return null;
  }
  return node;
}

/**
 * The twins of `element` (see the module comment), in document order, never
 * including `element` itself, its ancestors or descendants.
 */
export function findTwinElements(
  element: Element,
  { maxDepth = DEFAULT_MAX_DEPTH }: FindTwinElementsOptions = {},
): Element[] {
  const text = normalizeText(element.textContent);
  if (!text) return [];

  const root = element.ownerDocument.body ?? element.ownerDocument.documentElement;
  const seen = new Set<Element>([element]);
  const twins: Element[] = [];
  // Child indices from the current ancestor down to `element`.
  const path: number[] = [];
  let node: Element = element;

  for (let depth = 0; depth <= maxDepth; depth++) {
    if (node === root) break;
    const parent = node.parentElement;
    if (!parent) break;

    const nodeChildCount = node.children.length;
    const nodeText = depth === 0 ? text : normalizeText(node.textContent);

    for (const sibling of Array.from(parent.children)) {
      if (sibling === node || sibling.tagName !== node.tagName) continue;
      // Cheap structural filter before reading a possibly large subtree's text.
      if (sibling.children.length !== nodeChildCount) continue;
      if (normalizeText(sibling.textContent) !== nodeText) continue;

      const target = followPath(sibling, path);
      if (!target || seen.has(target)) continue;
      if (target.tagName !== element.tagName) continue;
      if (depth > 0 && normalizeText(target.textContent) !== text) continue;
      if (target.contains(element) || element.contains(target)) continue;
      if (target.closest(OVERLAY_SELECTOR)) continue;

      seen.add(target);
      twins.push(target);
    }

    path.unshift(Array.prototype.indexOf.call(parent.children, node));
    node = parent;
  }

  return sortInDocumentOrder(twins);
}

/**
 * `elements` plus the twins of each, de-duplicated, in document order. What a
 * selector's matches become before the personalized content is written.
 */
export function withTwins(
  elements: Element[],
  options?: FindTwinElementsOptions,
): Element[] {
  if (elements.length === 0) return elements;
  const all = new Set<Element>(elements);
  for (const element of elements) {
    for (const twin of findTwinElements(element, options)) all.add(twin);
  }
  if (all.size === elements.length) return elements;
  return sortInDocumentOrder(Array.from(all));
}

function sortInDocumentOrder(elements: Element[]): Element[] {
  return elements.sort((a, b) => {
    if (a === b) return 0;
    const position = a.compareDocumentPosition(b);
    return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });
}
